import React from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { Checkbox, CheckboxChangeEvent, CheckboxProps } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import { useTranslation } from '@hooks/index';

interface CoreCheckboxProps extends CheckboxProps {
    'data-testid': string;
    alternativeStyle?: boolean;
}

const CoreCheckbox = ({ className, alternativeStyle, ...rest }: CoreCheckboxProps) => (
    <Checkbox
        className={cn('!k-align-top', className, { 'tri-checkbox-secondary': alternativeStyle })}
        size="medium"
        {...rest}
        rounded="none"
    />
);

interface CheckboxControllerProps<TData, TControl extends FieldValues> extends CoreCheckboxProps {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (event: CheckboxChangeEvent) => void;
}

const CheckboxController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    'data-testid': dataTestId,
    required = false,
    label,
    maxLength,
    readOnly,
    validateRules,
    handleInputChange,
    ...props
}: CheckboxControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value, name: fieldName }, fieldState: { error } }) => {
                return (
                    <CoreCheckbox
                        {...props}
                        name={fieldName}
                        data-testid={dataTestId}
                        label={label}
                        disabled={disabled}
                        valid={!error}
                        value={value as string}
                        validationMessage={error?.message}
                        onChange={(event) => {
                            onChange(event.target.value);
                            if (handleInputChange) handleInputChange(event);
                        }}
                        readOnly={readOnly}
                        maxLength={maxLength}
                    />
                );
            }}
        />
    );
};

export { CheckboxController };
export default CoreCheckbox;
